import { z } from 'zod';
import { EMAIL_TEMPLATE_CATEGORIES, type EmailTemplateCategory } from '../constants';
import type { IsoDateTime } from './common';

export const EMAIL_TEMPLATE_CATEGORY_LABELS: Record<EmailTemplateCategory, string> = {
  introduction: 'Introduction',
  equipment_replacement: 'Equipment replacement',
  new_restaurant: 'New restaurant',
  quote_follow_up: 'Quote follow-up',
  check_in: 'Check-in',
  new_location: 'New location',
  post_meeting: 'After a meeting',
  custom: 'Custom',
};

export const TemplateListQuery = z.object({
  q: z.string().trim().max(200).optional(),
  category: z.enum(EMAIL_TEMPLATE_CATEGORIES).optional(),
  /** true → only active templates (pickers); omitted → active and inactive (management list). */
  active: z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true')),
});
export type TemplateListQuery = z.input<typeof TemplateListQuery>;

const TemplateFields = {
  name: z.string().trim().min(1, 'Name is required').max(200),
  category: z.enum(EMAIL_TEMPLATE_CATEGORIES).default('custom'),
  subject: z.string().trim().min(1, 'Subject is required').max(500),
  body: z.string().trim().min(1, 'Write the email body').max(50_000),
  isActive: z.boolean().default(true),
};

export const CreateTemplateBody = z.object(TemplateFields);
export type CreateTemplateBody = z.input<typeof CreateTemplateBody>;

export const UpdateTemplateBody = z.object({
  name: TemplateFields.name.optional(),
  category: z.enum(EMAIL_TEMPLATE_CATEGORIES).optional(),
  subject: TemplateFields.subject.optional(),
  body: TemplateFields.body.optional(),
  isActive: z.boolean().optional(),
  version: z.number().int().min(1),
});
export type UpdateTemplateBody = z.input<typeof UpdateTemplateBody>;

/**
 * Render a template for preview. With `businessId` (and optionally `contactId`) real lead data is used;
 * otherwise sample values. `subject`/`body` override the stored text so the editor can preview unsaved edits.
 */
export const TemplatePreviewBody = z.object({
  businessId: z.uuid().nullish(),
  contactId: z.uuid().nullish(),
  subject: z.string().max(500).optional(),
  body: z.string().max(50_000).optional(),
});
export type TemplatePreviewBody = z.input<typeof TemplatePreviewBody>;

/**
 * POST /api/templates/preview — render text that is not saved as a template (yet): the editor's live preview of
 * a new template, with sample values or a real lead's data like TemplatePreviewBody.
 */
export const TemplateDraftPreviewBody = TemplatePreviewBody.extend({
  subject: z.string().max(500),
  body: z.string().max(50_000),
});
export type TemplateDraftPreviewBody = z.input<typeof TemplateDraftPreviewBody>;

export type EmailTemplateDTO = {
  id: string;
  /** Stable key of seeded templates; null for user-created ones. */
  key: string | null;
  name: string;
  category: EmailTemplateCategory;
  subject: string;
  body: string;
  isActive: boolean;
  version: number;
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
  createdBy: string | null;
  updatedBy: string | null;
  /** Number of automated sequences with a step using this template. */
  usedBySequences: number;
};

export type TemplatePreviewDTO = {
  subject: string;
  text: string;
  html: string;
  missing: string[];
  unknown: string[];
  errors: string[];
  /** 'lead' when rendered with a real lead's data, 'sample' with example values. */
  dataSource: 'lead' | 'sample';
  /** With a real lead: who it was rendered for (the contact bulk email would pick unless one was given). */
  lead: { businessName: string; contactName: string | null; email: string | null } | null;
};

/** 400 `invalid_template` error details. */
export type TemplateValidationDetails = { unknownVariables: string[]; errors: string[] };
